// src/hooks/useSubscriptionGuard.js
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { checkSubscriptionStatus, checkUsageLimit } from '../services/usageService';

export const useSubscriptionGuard = () => {
    const { user } = useAuth();
    const [subscription, setSubscription] = useState(null);
    const [isValid, setIsValid] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [usage, setUsage] = useState(null);

    const validateSubscription = async () => {
        setIsLoading(true);
        try {
            const subData = await checkSubscriptionStatus();
            setSubscription(subData);

            if (subData && subData.plan?.name !== 'free') {
                const isActive = subData.status === 'active';
                const isNotExpired = subData.endDate ? new Date(subData.endDate) > new Date() : true;

                if (!isActive || !isNotExpired) {
                    setIsValid(false);
                    setError({
                        type: 'EXPIRED',
                        message: 'Your subscription has expired. Please renew to continue.',
                        endDate: subData.endDate
                    });
                } else {
                    setIsValid(true);
                    setError(null);
                }
            } else {
                // Free plan - check usage limits
                const usageData = await checkUsageLimit('incident');
                setUsage(usageData);

                if (!usageData.allowed) {
                    setIsValid(false);
                    setError({
                        type: 'LIMIT_REACHED',
                        message: `You have reached your monthly limit of ${usageData.limit} incidents. Please upgrade to continue.`,
                        limit: usageData.limit,
                        currentUsage: usageData.currentUsage
                    });
                } else {
                    setIsValid(true);
                    setError(null);
                }
            }
        } catch (err) {
            console.error('Validation error:', err);
            setIsValid(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            validateSubscription();
        }
    }, [user]);

    return { subscription, isValid, isLoading, error, usage, validateSubscription };
};